import * as styles from './Styles';
import type { Feature } from 'ol';
import Constants from './Constants';

export interface Einzugsgebiet {
  type: number, //SETyp von Constants
  groesseGesamt: number, //Gesamte Größe des Einzugsgebeits, inklusive desjenigen, das zum Transportelement gehört
  groesseOhneEigenTG: number, //Größe des Einzugsgebeits, ohne dasjenige, das zum Transportelement gehört (wird bei Bauwerken nicht genutzt)
  SEBezeichnung: string, //ID_SEBezeichnung des Transportelements
  tgArray: Array<Feature>, //Array mit den Teileinzugsgebieten bzw. bei Bauwerk kanalisierten Flächen
  warn: undefined | string //Warnung, falls eines der Teilgebiete keine Info über Flächengröße besitzt
}

export interface ClickInfo {
  visible: boolean,
  weiterfuehrendeAktion: boolean,
  bauwerke: Array<Feature>,
  teilgebiete: Array<Feature>,
  transportelemente: Array<Feature>,
  kanalisierteFlaechen: Array<Feature>,
  gewaesser: Feature,
  gewaesserKm: number,
  einzugsgebiet: Einzugsgebiet | undefined,
  abfluss: object | undefined
}

export class ClickInfo {

  constructor() {
    this.visible = false;
    this.weiterfuehrendeAktion = false;
    this.bauwerke = [];
    this.teilgebiete = [];
    this.transportelemente = [];
    this.kanalisierteFlaechen = [];
    this.gewaesser;
    this.gewaesserKm;
    this.einzugsgebiet;
    this.abfluss;
  }

  /**
   * setEinzugsgebiet fügt dem ClickInfo ein Objekt mit den Infos zum Einzugsgebiet hinzu, wenn der Schalter "zeige Einzugsgebiet" für ein Transportelement betätigt wird.
   * @param SEBezeichnung ID_SEBezeichnung des Transportelements
   */
  setEinzugsgebiet(SEBezeichnung: string, type: number) {
    this.einzugsgebiet = {
      type: type,
      groesseGesamt: 0,
      groesseOhneEigenTG: 0,
      SEBezeichnung: SEBezeichnung,
      tgArray: [],
      warn: undefined
    }
  }

  emptyEinzugsgebiet() {
    this.einzugsgebiet?.tgArray.map((item) => {
      if (item.get('ID_TG_Typ') === "natürliches TG") {
        item.setStyle(styles.tgStyle);
      } else {
        item.setStyle(styles.setKfStyle(item));
      }

    })
    this.einzugsgebiet = undefined;
  }

  pushTGForEinzugsbebiet(tg: Feature, seTyp: number) {
    if (!this.einzugsgebiet) {
      console.warn("Das Einzugsbiet für das ClickInfo Objekt ist noch undefined")
      return;
    }
    this.einzugsgebiet.tgArray.push(tg);
    const tgGroesse = tg.get('ID_TG_Groesse_ha');
    if (tgGroesse) {
      this.einzugsgebiet.groesseGesamt += tgGroesse;
      if (seTyp == Constants.SETypen.Transportelement && tg.get('ID_SEBezeichnung') !== this.einzugsgebiet.SEBezeichnung) {
        this.einzugsgebiet.groesseOhneEigenTG += tgGroesse;
      }
    } else {
      this.einzugsgebiet['warn'] = 'Hinweis: Mindestens eine Teilfläche enthält keine Information über die Größe.'
    }
    
  }

  setVisible() {
    this.visible = true;
  }

  setInvisible() {
    this.visible = false;
  }

  pushBauwerk(bw: Feature) {
    this.bauwerke.push(bw);
  }

  emptyBauwerke() {
    this.bauwerke.map((item) => {
      item?.setStyle(styles.setBauwerkStyle(item));
    })
    this.bauwerke = [];
  }

  pushTeilgebiet(tg: Feature) {
    if (this.teilgebiete.indexOf(tg) === -1) {
      this.teilgebiete.push(tg);
    }
  }

  emptyTeilgebiete() {
    this.teilgebiete.map((item) => {
      item.setStyle(styles.tgStyle);
    })
    this.teilgebiete = [];
  }

  pushTransportelement(te: Feature) {
    if (!this.transportelemente.some(element => element.getId() === te.getId())) {
      this.transportelemente.push(te);
    }
  }

  emptyTransportelemente() {
    this.transportelemente.map((item) => {
      item.setStyle(styles.teStyle);
    })
    this.transportelemente = [];
  }

  pushKanalisierteFlaeche(kF: Feature) {
    this.kanalisierteFlaechen.push(kF);
  }

  emptyKanalFlaechen() {
    this.kanalisierteFlaechen.map((item) => {
      item.setStyle(styles.setKfStyle(item));
    })
    this.kanalisierteFlaechen = [];
  }

  setGewaesser(gw: Feature) {
    this.gewaesser = gw;
  }

  emptyGewaesser() {
    this.gewaesser = undefined;
  }

  getGewaesser() {
    return this.gewaesser;
  }

  getGewaesserKm() {
    if (this.gewaesserKm) {
      return this.gewaesserKm.toLocaleString("de-DE", {minimumFractionDigits: 2});
    } else {
      return undefined;
    }
  }

  setGewaesserKm(km: number) {
    this.gewaesserKm = km;
  }

  setAbfluss(abfluss: object) {
    this.abfluss = abfluss;
  }

  getAbfluss() {
    if (!this.abfluss || Object.values(this.abfluss).every(el => el === undefined)) {
      return undefined;
    }
    return this.abfluss;
  }

  highlightSelection() {
    this.bauwerke.map((item) => {
      item.setStyle(styles.setBauwerkSelectStyle(item));
    })

    this.teilgebiete.map((item) => {
      item.setStyle(styles.tgSelectStyle);
    })

    this.transportelemente.map((item) => {
      item.setStyle(styles.teSelectStyle);
    })

    this.kanalisierteFlaechen.map((item) => {
      item.setStyle(styles.setKfSelectStyle(item));
    })
  }

  emptyAll() {
    this.emptyTeilgebiete();
    this.emptyBauwerke();
    this.emptyTransportelemente();
    this.emptyKanalFlaechen();
    this.emptyGewaesser();
    this.emptyEinzugsgebiet();
  }

  emptyAllExceptGewaesser() {
    this.emptyTeilgebiete();
    this.emptyBauwerke();
    this.emptyTransportelemente();
    this.emptyKanalFlaechen();
    this.emptyEinzugsgebiet();
  }

}